import React from 'react';
import { LAYERS } from '@/utils/layers';
import { useMobileDetection } from '@/contexts/MobileDetectionContext';

interface GestureOverlayProps {
  children?: React.ReactNode;
  zIndex?: number;
  className?: string;
  style?: React.CSSProperties;
  enabled?: boolean;
}

/**
 * Reusable gesture overlay component with proper z-index layering
 * Provides consistent gesture layer positioning across all dialogs
 */
export const GestureOverlay: React.FC<GestureOverlayProps> = ({
  children,
  zIndex = LAYERS.DIALOG_GESTURES,
  className = '',
  style = {},
  enabled = true
}) => {
  const { isMobile } = useMobileDetection();

  // Only render on mobile when enabled
  if (!isMobile || !enabled) {
    return null;
  }

  return (
    <div 
      className={`absolute inset-0 pointer-events-none ${className}`}
      style={{ 
        zIndex,
        touchAction: 'pan-y pan-x', // Enable gesture recognition
        ...style 
      }}
    >
      {children}
    </div>
  );
};

interface GestureHintProps {
  message: string;
  isVisible: boolean;
  position?: 'top' | 'bottom' | 'center';
  className?: string;
}

/**
 * Gesture hint component with consistent styling
 */
export const GestureHint: React.FC<GestureHintProps> = ({
  message,
  isVisible,
  position = 'bottom',
  className = ''
}) => {
  const positionClasses = {
    top: '-top-6',
    bottom: '-bottom-6',
    center: 'top-1/2 -translate-y-1/2'
  };

  return (
    <div 
      className={`absolute ${positionClasses[position]} left-0 right-0 text-center text-xs text-muted-foreground transition-opacity duration-1000 pointer-events-none ${
        isVisible ? 'opacity-100' : 'opacity-0'
      } ${className}`}
    >
      <span className="hover:text-foreground transition-colors">
        {message}
      </span>
    </div>
  );
};

export default GestureOverlay;